import Papa from "papaparse";

import { NONE_VERIFICATION_VALUE, RegistrationContactType } from "@/types";

export interface CsvValidationError {
  type: "missing_column" | "empty_value" | "invalid_format" | "empty_file";
  message: string;
  row?: number;
  column?: string;
}

export interface CsvValidationResult {
  valid: boolean;
  errors: CsvValidationError[];
  rowCount: number;
}

/**
 * Determines which columns are required based on registration contact type
 * and verification field configuration.
 */
const getRequiredColumns = (
  registrationContactType: RegistrationContactType | undefined,
  verificationField: string | undefined,
): { required: string[]; optional: string[] } => {
  const hasVerification =
    verificationField && verificationField !== NONE_VERIFICATION_VALUE;

  switch (registrationContactType) {
    case "EMAIL":
      return {
        required: hasVerification
          ? ["email", "id", "amount", "verification"]
          : ["email", "id", "amount"],
        optional: ["paymentID"],
      };
    case "PHONE_NUMBER":
      return {
        required: hasVerification
          ? ["phone", "id", "amount", "verification"]
          : ["phone", "id", "amount"],
        optional: ["paymentID"],
      };
    case "EMAIL_AND_WALLET_ADDRESS":
      return {
        required: ["email", "walletAddress", "id", "amount"],
        optional: ["walletAddressMemo", "paymentID"],
      };
    case "PHONE_NUMBER_AND_WALLET_ADDRESS":
      return {
        required: ["phone", "walletAddress", "id", "amount"],
        optional: ["walletAddressMemo", "paymentID"],
      };
    default:
      return { required: ["id", "amount"], optional: [] };
  }
};

const PHONE_REGEX = /^\+[1-9]\d{1,14}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STELLAR_ADDRESS_REGEX = /^G[A-Z2-7]{55}$/;
const DATE_OF_BIRTH_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const YEAR_MONTH_REGEX = /^\d{4}-\d{2}$/;
const PIN_REGEX = /^[A-Za-z0-9]{4,8}$/;

const isValidDateOfBirth = (value: string): boolean => {
  if (!DATE_OF_BIRTH_REGEX.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return (
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === m - 1 &&
    date.getUTCDate() === d
  );
};

const isValidYearMonth = (value: string): boolean => {
  if (!YEAR_MONTH_REGEX.test(value)) return false;
  const [, m] = value.split("-").map(Number);
  return m >= 1 && m <= 12;
};

const validateVerificationValue = (
  value: string,
  verificationField: string | undefined,
): string | null => {
  switch (verificationField) {
    case "DATE_OF_BIRTH":
      return isValidDateOfBirth(value) ? null : 'must be a valid date in "YYYY-MM-DD" format';
    case "YEAR_MONTH":
      return isValidYearMonth(value) ? null : 'must be in "YYYY-MM" format with a valid month';
    case "PIN":
      return PIN_REGEX.test(value)
        ? null
        : "must be 4–8 alphanumeric characters (case sensitive)";
    case "NATIONAL_ID_NUMBER":
    default:
      return null;
  }
};

/**
 * Validates the CSV file content matches the expected format for CSV disbursements.
 */
export const validateCsvFile = (
  file: File,
  registrationContactType: RegistrationContactType | undefined,
  verificationField: string | undefined,
): Promise<CsvValidationResult> => {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const errors: CsvValidationError[] = [];
        const data = result.data as Record<string, string>[];

        if (data.length === 0) {
          resolve({ valid: false, errors: [{ type: "empty_file", message: "CSV file is empty or contains no data rows." }], rowCount: 0 });
          return;
        }

        const { required } = getRequiredColumns(registrationContactType, verificationField);
        const headers = Object.keys(data[0]);

        // Check required columns exist
        for (const col of required) {
          if (!headers.includes(col)) {
            errors.push({
              type: "missing_column",
              message: `Missing required column: "${col}"`,
              column: col,
            });
          }
        }

        if (errors.length > 0) {
          resolve({ valid: false, errors, rowCount: data.length });
          return;
        }

        // Validate row data (limit to first 100 errors)
        const MAX_ERRORS = 100;
        for (let i = 0; i < data.length && errors.length < MAX_ERRORS; i++) {
          const row = data[i];
          const rowNum = i + 2; // +2 because row 1 is header, data is 0-indexed

          // Check required fields are not empty
          for (const col of required) {
            const value = row[col]?.trim();
            if (!value) {
              errors.push({
                type: "empty_value",
                message: `Row ${rowNum}: "${col}" is empty.`,
                row: rowNum,
                column: col,
              });
            }
          }

          // Validate amount is a positive number
          const amount = row["amount"]?.trim();
          if (amount) {
            const num = Number(amount);
            if (isNaN(num) || num <= 0) {
              errors.push({
                type: "invalid_format",
                message: `Row ${rowNum}: "amount" must be a positive number, got "${amount}".`,
                row: rowNum,
                column: "amount",
              });
            }
          }

          // Validate phone format
          if (headers.includes("phone")) {
            const phone = row["phone"]?.trim();
            if (phone && !PHONE_REGEX.test(phone)) {
              errors.push({
                type: "invalid_format",
                message: `Row ${rowNum}: "phone" must be in international format starting with +, got "${phone}".`,
                row: rowNum,
                column: "phone",
              });
            }
          }

          // Validate email format
          if (headers.includes("email")) {
            const email = row["email"]?.trim();
            if (email && !EMAIL_REGEX.test(email)) {
              errors.push({
                type: "invalid_format",
                message: `Row ${rowNum}: "email" is not a valid email address.`,
                row: rowNum,
                column: "email",
              });
            }
          }

          // Validate wallet address format
          if (headers.includes("walletAddress")) {
            const addr = row["walletAddress"]?.trim();
            if (addr && !STELLAR_ADDRESS_REGEX.test(addr)) {
              errors.push({
                type: "invalid_format",
                message: `Row ${rowNum}: "walletAddress" is not a valid Stellar address.`,
                row: rowNum,
                column: "walletAddress",
              });
            }
          }

          // Validate verification value matches the chosen verification method
          if (
            headers.includes("verification") &&
            verificationField &&
            verificationField !== NONE_VERIFICATION_VALUE
          ) {
            const verificationValue = row["verification"]?.trim();
            if (verificationValue) {
              const reason = validateVerificationValue(verificationValue, verificationField);
              if (reason) {
                errors.push({
                  type: "invalid_format",
                  message: `Row ${rowNum}: "verification" ${reason}, got "${verificationValue}".`,
                  row: rowNum,
                  column: "verification",
                });
              }
            }
          }
        }

        resolve({
          valid: errors.length === 0,
          errors,
          rowCount: data.length,
        });
      },
      error: () => {
        resolve({
          valid: false,
          errors: [{ type: "invalid_format", message: "Failed to parse CSV file." }],
          rowCount: 0,
        });
      },
    });
  });
};

